import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { formatDate, formatRupiah, REMINDER_META } from "@/lib/format";
import { Truck, Plus, Loader2, Pencil, Trash2, MapPin, ShieldCheck, Paperclip, CheckCircle2, History } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileUploader } from "@/components/FileUploader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { useBulk, BulkCheck, BulkCheckAll, BulkBar } from "@/components/BulkDelete";
import { useAutoRefresh } from "@/lib/autoRefresh";

const STATUS_LABEL = { aman: "Aman", segera: "Segera", terlambat: "Terlambat" };
const vehicleStatus = (v) => {
  let st = "aman";
  for (const r of v.reminders || []) {
    if (r.status === "overdue" || r.status === "perlu_diskusi") return "terlambat";
    if (r.status === "upcoming") st = "segera";
  }
  return st;
};

function ReminderPill({ item }) {
  const m = REMINDER_META[item.status] || REMINDER_META.unknown;
  return (
    <div className={`rounded-lg border px-2.5 py-1.5 ${m.cls}`} data-testid={`reminder-${item.key}`}>
      <p className="text-[11px] font-semibold uppercase tracking-wide">{item.label}</p>
      <p className="text-xs">{item.due_date ? formatDate(item.due_date) : "—"}</p>
      <p className="text-[10px] font-medium">
        {item.status === "overdue" ? `Terlambat ${Math.abs(item.days)} hr`
          : item.status === "perlu_diskusi" ? "Perlu Diskusi"
          : item.status === "upcoming" ? `${item.days} hari lagi`
          : item.status === "ok" ? "Aman" : "Belum diisi"}
      </p>
      {item.cost > 0 && <p className="text-[10px] font-mono mt-0.5 opacity-80">{formatRupiah(item.cost)}</p>}
    </div>
  );
}

export default function Vehicles() {
  const { user } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const canManage = ["tim_kendaraan", "it_admin"].includes(user?.role);
  const canDelete = user?.role === "it_admin";
  const bulk = useBulk();
  const [search, setSearch] = useState("");
  const [branchFilter, setBranchFilter] = useState("all");
  const [params, setParams] = useSearchParams();
  const statusFilter = STATUS_LABEL[params.get("status")] ? params.get("status") : "all";
  const setStatusFilter = (s) => setParams(s === "all" ? {} : { status: s });

  const load = useCallback((silent) => {
    if (silent !== true) setLoading(true);
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);
  useAutoRefresh(load);

  const del = async (id) => {
    try { await api.delete(`/vehicles/${id}`); toast.success("Kendaraan dihapus."); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const branchOptions = Array.from(new Set(vehicles.map((v) => v.branch || "Tanpa Cabang"))).sort();
  const filtered = vehicles.filter((v) =>
    (branchFilter === "all" || (v.branch || "Tanpa Cabang") === branchFilter) &&
    (statusFilter === "all" || vehicleStatus(v) === statusFilter) &&
    (!search.trim() || (v.plate_number || "").toLowerCase().includes(search.trim().toLowerCase())));

  const byBranch = filtered.reduce((acc, v) => {
    const b = v.branch || "Tanpa Cabang";
    (acc[b] = acc[b] || []).push(v);
    return acc;
  }, {});

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Truck className="h-6 w-6 text-brand" /> Data Kendaraan
          </h2>
          <p className="text-sm text-muted-foreground">Kelola kendaraan & reminder pajak, KIR, servis, dan ganti oli</p>
        </div>
        {canManage && <VehicleDialog onDone={load} />}
      </div>

      <div className="flex flex-wrap items-center gap-3" data-testid="vehicle-filter-bar">
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nomor plat..."
          className="max-w-xs h-9" data-testid="vehicle-search-input" />
        <select value={branchFilter} onChange={(e) => setBranchFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="vehicle-branch-filter">
          <option value="all">Semua Cabang</option>
          {branchOptions.map((b) => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="vehicle-status-filter">
          <option value="all">Semua Status</option>
          {Object.entries(STATUS_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        {(search || branchFilter !== "all" || statusFilter !== "all") && (
          <Button variant="ghost" size="sm" onClick={() => { setSearch(""); setBranchFilter("all"); setStatusFilter("all"); }} data-testid="vehicle-filter-reset">Reset</Button>
        )}
        <span className="text-xs text-muted-foreground ml-auto">{filtered.length} dari {vehicles.length} unit</span>
      </div>

      {loading ? (
        <div className="space-y-2.5 py-4" data-testid="vehicles-loading">{[0, 1, 2, 3].map((i) => (<div key={i} className={`h-4 rounded-md skeleton-shimmer animate-fade-in-up stagger-${i + 1}`} />))}</div>
      ) : vehicles.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground">Belum ada data kendaraan.</Card>
      ) : filtered.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground" data-testid="vehicles-filter-empty">Tidak ada kendaraan sesuai filter.</Card>
      ) : (
        <>
        <BulkBar bulk={bulk} onDone={load} label="kendaraan" />
        {Object.entries(byBranch).map(([branch, list]) => (
          <div key={branch} data-testid={`branch-group-${branch}`}>
            <div className="flex items-center gap-2 mb-3">
              <BulkCheckAll bulk={bulk} entries={list.map((v) => [v.id, { kind: "vehicles", id: v.id }])} testId={`bulk-vehicle-all-${branch}`} />
              <MapPin className="h-4 w-4 text-brand" />
              <h3 className="font-semibold" style={{ fontFamily: "Outfit" }}>{branch}</h3>
              <span className="text-xs text-muted-foreground">({list.length} unit)</span>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {list.map((v) => (
                <Card key={v.id} className="p-5" data-testid={`vehicle-card-${v.id}`}>
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-start gap-3">
                    <span className="pt-1.5"><BulkCheck bulk={bulk} k={v.id} item={{ kind: "vehicles", id: v.id }} testId={`bulk-vehicle-${v.id}`} /></span>
                    <div>
                      <p className="font-semibold text-lg" style={{ fontFamily: "Outfit" }}>{v.plate_number}</p>
                      <p className="text-sm text-muted-foreground">{v.vehicle_type}</p>
                      {v.driver_name && <p className="text-xs text-muted-foreground mt-0.5" data-testid={`vehicle-driver-${v.id}`}>Sopir: <span className="font-medium text-foreground/80">{v.driver_name}</span></p>}
                      {v.vehicle_year && <p className="text-xs text-muted-foreground mt-0.5">Tahun: {v.vehicle_year}{v.vehicle_age != null ? ` · ${v.vehicle_age} th` : ""}</p>}
                      {v.age_warning && (
                        <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700 border border-red-200" data-testid={`vehicle-age-badge-${v.id}`}>
                          <ShieldCheck className="h-3 w-3" /> ≥ 8 th — perlu evaluasi
                        </span>
                      )}
                    </div>
                    </div>
                    {canManage && (
                      <div className="flex gap-1">
                        <VehicleDialog vehicle={v} onDone={load} />
                        {canDelete && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-600" data-testid={`delete-vehicle-btn-${v.id}`}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Hapus kendaraan {v.plate_number}?</AlertDialogTitle>
                                <AlertDialogDescription>Tindakan ini tidak dapat dibatalkan.</AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Batal</AlertDialogCancel>
                                <AlertDialogAction onClick={() => del(v.id)} className="bg-rose-600 hover:bg-rose-700" data-testid={`confirm-delete-vehicle-${v.id}`}>Hapus</AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {v.reminders.map((it) => <ReminderPill key={it.key} item={it} />)}
                  </div>
                  {v.total_est_cost > 0 && (
                    <p className="text-xs text-muted-foreground mt-3" data-testid={`vehicle-total-cost-${v.id}`}>
                      Total perkiraan biaya: <span className="font-mono text-brand font-semibold">{formatRupiah(v.total_est_cost)}</span>
                    </p>
                  )}
                  {v.notes && <p className="text-xs text-muted-foreground mt-3">{v.notes}</p>}
                  <div className="mt-3">
                    <Link to={`/vehicles/${v.id}`} data-testid={`view-history-link-${v.id}`}>
                      <Button variant="outline" size="sm" className="w-full border-brand/40 text-brand hover:bg-secondary h-8">
                        <History className="h-4 w-4 mr-1.5" /> Lihat Riwayat Lengkap
                      </Button>
                    </Link>
                  </div>
                  {canManage && (
                    <div className="mt-4 pt-3 border-t">
                      <Link to="/expenses" className="text-xs text-brand hover:underline" data-testid={`goto-expense-${v.id}`}>Catat pengerjaan & bukti lewat menu Pengeluaran →</Link>
                    </div>
                  )}
                  {(v.maintenance_records || []).length > 0 && (
                    <div className="mt-2 space-y-1.5" data-testid={`maint-list-${v.id}`}>
                      {v.maintenance_records.slice().reverse().map((rec) => (
                        <div key={rec.id} className="flex items-center justify-between gap-2 text-xs rounded-md bg-emerald-50 border border-emerald-100 px-2.5 py-1.5">
                          <div className="min-w-0">
                            <span className="font-medium text-emerald-800">{rec.label}</span>
                            <span className="text-muted-foreground"> · {formatDate(rec.date)}{rec.actual_cost > 0 ? ` · ${formatRupiah(rec.actual_cost)}` : ""}</span>
                            {rec.next_due && <span className="text-muted-foreground"> · Jatuh tempo: {formatDate(rec.next_due)}</span>}
                          </div>
                          {rec.file_id ? (
                            <a href={fileUrl(rec.file_id)} target="_blank" rel="noreferrer"
                              className="flex items-center gap-1 text-brand hover:underline shrink-0" data-testid={`maint-proof-${rec.id}`}>
                              <Paperclip className="h-3 w-3" /> Bukti
                            </a>
                          ) : (
                            <span className="flex items-center gap-1 text-emerald-600 shrink-0" data-testid={`maint-done-${rec.id}`}>
                              <CheckCircle2 className="h-3 w-3" /> Selesai
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        ))}
        </>
      )}
    </div>
  );
}

function MaintenanceDialog({ vehicle, onDone }) {
  const [open, setOpen] = useState(false);
  const [itemKey, setItemKey] = useState("pajak");
  const [date, setDate] = useState("");
  const [cost, setCost] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const isRenewal = itemKey === "pajak" || itemKey === "kir";

  const reset = () => { setItemKey("pajak"); setDate(""); setCost(""); setNote(""); setFiles([]); };

  const submit = async (e) => {
    e.preventDefault();
    if (!files.length) { toast.error("Upload file bukti terlebih dahulu"); return; }
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/maintenance`, {
        item_key: itemKey, file_id: files[0].file_id,
        date: date || undefined, actual_cost: Number(cost) || 0, note,
      });
      toast.success("Bukti pengerjaan tersimpan & reminder diperbarui.");
      setOpen(false); reset(); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" className="border-brand text-brand hover:bg-secondary h-8" data-testid={`add-maint-btn-${vehicle.id}`}>
          <ShieldCheck className="h-4 w-4 mr-1.5" /> Catat Bukti
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "Outfit" }}>Bukti Pengerjaan — {vehicle.plate_number}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Jenis</Label>
            <Select value={itemKey} onValueChange={setItemKey}>
              <SelectTrigger className="mt-1.5" data-testid="maint-item-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="pajak">Pajak</SelectItem>
                <SelectItem value="kir">KIR</SelectItem>
                <SelectItem value="servis">Servis</SelectItem>
                <SelectItem value="oli">Ganti Oli</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>{isRenewal ? "Berlaku s/d (baru)" : "Tanggal Dikerjakan"}</Label>
              <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" data-testid="maint-date-input" />
              <p className="text-[11px] text-muted-foreground mt-1">{isRenewal ? "Kosong = hari ini" : "Kosong = hari ini"}</p>
            </div>
            <div>
              <Label>Biaya Aktual</Label>
              <Input type="number" min="0" value={cost} onChange={(e) => setCost(e.target.value)} placeholder="0" className="mt-1.5" data-testid="maint-cost-input" />
            </div>
          </div>
          <div>
            <Label>Keterangan</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} className="mt-1.5" placeholder="Opsional" data-testid="maint-note-input" />
          </div>
          <div>
            <Label>Bukti (foto struk / dokumen) <span className="text-red-500">*</span></Label>
            <div className="mt-1.5">
              <FileUploader files={files} onChange={setFiles} testId={`maint-uploader-${vehicle.id}`} multiple={false} />
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid={`submit-maint-btn-${vehicle.id}`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan Bukti"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export function VehicleDialog({ vehicle, onDone }) {
  const editing = !!vehicle;
  const [open, setOpen] = useState(false);
  const [branches, setBranches] = useState([]);
  const [form, setForm] = useState({
    vehicle_type: "", plate_number: "", driver_name: "", branch: "", branch_id: "", vehicle_year: "",
    tax_date: "", kir_date: "", last_service_date: "", last_oil_change_date: "", last_oil_odometer: "",
    est_tax_cost: "", est_kir_cost: "", est_service_cost: "", est_oil_cost: "", notes: "",
    has_credit: false, credit_lessor: "", credit_total_price: "", credit_monthly: "",
    credit_due_date: "", credit_months_remaining: "", credit_payment_method: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) api.get("/branches").then((r) => setBranches(r.data)).catch(() => {});
    if (open && vehicle) {
      setForm({
        vehicle_type: vehicle.vehicle_type || "", plate_number: vehicle.plate_number || "",
        driver_name: vehicle.driver_name || "",
        branch: vehicle.branch || "", branch_id: vehicle.branch_id || "",
        vehicle_year: vehicle.vehicle_year || "",
        tax_date: (vehicle.tax_date || "").slice(0, 10),
        kir_date: (vehicle.kir_date || "").slice(0, 10),
        last_service_date: (vehicle.last_service_date || "").slice(0, 10),
        last_oil_change_date: (vehicle.last_oil_change_date || "").slice(0, 10),
        last_oil_odometer: vehicle.last_oil_odometer ?? "",
        est_tax_cost: vehicle.est_tax_cost || "", est_kir_cost: vehicle.est_kir_cost || "",
        est_service_cost: vehicle.est_service_cost || "", est_oil_cost: vehicle.est_oil_cost || "",
        has_credit: !!vehicle.has_credit, credit_lessor: vehicle.credit_lessor || "",
        credit_total_price: vehicle.credit_total_price || "", credit_monthly: vehicle.credit_monthly || "",
        credit_due_date: (vehicle.credit_due_date || "").slice(0, 10),
        credit_months_remaining: vehicle.credit_months_remaining || "",
        credit_payment_method: vehicle.credit_payment_method || "",
        notes: vehicle.notes || "",
      });
    } else if (open) {
      setForm({
        vehicle_type: "", plate_number: "", driver_name: "", branch: "", branch_id: "", vehicle_year: "",
        tax_date: "", kir_date: "", last_service_date: "", last_oil_change_date: "", last_oil_odometer: "",
        est_tax_cost: "", est_kir_cost: "", est_service_cost: "", est_oil_cost: "", notes: "",
        has_credit: false, credit_lessor: "", credit_total_price: "", credit_monthly: "",
        credit_due_date: "", credit_months_remaining: "", credit_payment_method: "",
      });
    }
  }, [open, vehicle]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.vehicle_type.trim() || !form.plate_number.trim()) {
      toast.error("Isi jenis kendaraan dan plat nomor"); return;
    }
    if (!form.branch_id) { toast.error("Pilih cabang kendaraan"); return; }
    setBusy(true);
    const payload = {
      ...form,
      vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
      last_oil_odometer: form.last_oil_odometer !== "" && form.last_oil_odometer != null ? Number(form.last_oil_odometer) : null,
      est_tax_cost: Number(form.est_tax_cost) || 0,
      est_kir_cost: Number(form.est_kir_cost) || 0,
      est_service_cost: Number(form.est_service_cost) || 0,
      est_oil_cost: Number(form.est_oil_cost) || 0,
      has_credit: !!form.has_credit,
      credit_total_price: Number(form.credit_total_price) || 0,
      credit_monthly: Number(form.credit_monthly) || 0,
      credit_months_remaining: Number(form.credit_months_remaining) || 0,
      credit_due_date: form.credit_due_date || null,
      credit_lessor: form.credit_lessor || "",
      credit_payment_method: form.credit_payment_method || "",
    };
    try {
      if (editing) await api.put(`/vehicles/${vehicle.id}`, payload);
      else await api.post("/vehicles", payload);
      toast.success(editing ? "Kendaraan diperbarui." : "Kendaraan ditambahkan.");
      setOpen(false); onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {editing ? (
          <Button size="icon" variant="ghost" className="h-8 w-8 text-brand" data-testid={`edit-vehicle-btn-${vehicle.id}`}><Pencil className="h-4 w-4" /></Button>
        ) : (
          <Button className="bg-brand hover:bg-emerald-800" data-testid="add-vehicle-btn"><Plus className="h-4 w-4 mr-2" /> Tambah Kendaraan</Button>
        )}
      </DialogTrigger>
      <DialogContent data-testid="vehicle-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Kendaraan" : "Tambah Kendaraan"}</DialogTitle>
          <DialogDescription>Pilih cabang secara manual dan isi nama sopir kendaraan.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Jenis Kendaraan</Label>
              <Input value={form.vehicle_type} onChange={set("vehicle_type")} className="mt-1.5" data-testid="vehicle-type-input" placeholder="Truk / Mobil / Motor" />
            </div>
            <div>
              <Label>Plat Nomor</Label>
              <Input value={form.plate_number} onChange={set("plate_number")} className="mt-1.5" data-testid="vehicle-plate-input" placeholder="B 1234 XYZ" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Lokasi Cabang</Label>
              <select value={form.branch_id} onChange={set("branch_id")}
                className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="vehicle-branch-select">
                <option value="">Pilih cabang</option>
                {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </div>
            <div>
              <Label>Nama Sopir</Label>
              <Input value={form.driver_name} onChange={set("driver_name")} className="mt-1.5" data-testid="vehicle-driver-input" placeholder="Nama sopir kendaraan" />
            </div>
            <div>
              <Label>Tahun Kendaraan</Label>
              <Input type="number" min="1980" max="2100" value={form.vehicle_year} onChange={set("vehicle_year")} className="mt-1.5" data-testid="vehicle-year-input" placeholder="mis. 2018" />
              <p className="text-[11px] text-muted-foreground mt-1">Umur dihitung otomatis; ≥ 8 th ditandai perlu evaluasi.</p>
            </div>
          </div>
          <div className="rounded-lg border p-3 space-y-3" data-testid="vehicle-credit-section">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.has_credit} onChange={(e) => setForm((f) => ({ ...f, has_credit: e.target.checked }))} data-testid="vehicle-credit-toggle" className="h-4 w-4 accent-emerald-700" />
              <span className="text-sm font-semibold">Data Kredit Kendaraan (isi bila masih kredit)</span>
            </label>
            {form.has_credit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Nama Leasing / Kreditur</Label><Input value={form.credit_lessor} onChange={set("credit_lessor")} className="mt-1.5" data-testid="credit-lessor-input" /></div>
                <div><Label>Total Harga Kendaraan</Label><Input type="number" value={form.credit_total_price} onChange={set("credit_total_price")} className="mt-1.5" data-testid="credit-total-input" placeholder="0" /></div>
                <div><Label>Cicilan / Bulan</Label><Input type="number" value={form.credit_monthly} onChange={set("credit_monthly")} className="mt-1.5" data-testid="credit-monthly-input" placeholder="0" /></div>
                <div><Label>Sisa Bulan Cicilan</Label><Input type="number" value={form.credit_months_remaining} onChange={set("credit_months_remaining")} className="mt-1.5" data-testid="credit-months-input" placeholder="0" /></div>
                <div><Label>Tanggal Jatuh Tempo Cicilan</Label><Input type="date" value={form.credit_due_date} onChange={set("credit_due_date")} className="mt-1.5" data-testid="credit-due-input" /></div>
                <div>
                  <Label>Cara Bayar</Label>
                  <select value={form.credit_payment_method} onChange={set("credit_payment_method")} className="mt-1.5 h-10 w-full rounded-md border border-input bg-background px-3 text-sm" data-testid="credit-method-select">
                    <option value="">Pilih</option>
                    <option value="auto_debit">Auto Debit</option>
                    <option value="transfer">Transfer</option>
                    <option value="cash">Cash</option>
                  </select>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label className="text-sm font-semibold text-brand">Jadwal & Perkiraan Biaya</Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {[
                ["Pajak", "tax_date", "vehicle-tax-input", "est_tax_cost", "vehicle-tax-cost-input"],
                ["KIR", "kir_date", "vehicle-kir-input", "est_kir_cost", "vehicle-kir-cost-input"],
                ["Servis Terakhir", "last_service_date", "vehicle-service-input", "est_service_cost", "vehicle-service-cost-input"],
                ["Ganti Oli Terakhir", "last_oil_change_date", "vehicle-oil-input", "est_oil_cost", "vehicle-oil-cost-input"],
              ].map(([label, dateKey, dateTid, costKey, costTid]) => (
                <div key={dateKey} className="rounded-lg border p-3 space-y-2">
                  <div>
                    <Label className="text-xs">{label}</Label>
                    <Input type="date" value={form[dateKey]} onChange={set(dateKey)} className="mt-1" data-testid={dateTid} />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Perkiraan Biaya</Label>
                    <Input type="number" min="0" value={form[costKey]} onChange={set(costKey)} className="mt-1" placeholder="0" data-testid={costTid} />
                    {Number(form[costKey]) > 0 && <p className="text-[11px] text-brand font-mono mt-0.5">{formatRupiah(Number(form[costKey]))}</p>}
                  </div>
                  {dateKey === "last_oil_change_date" && (
                    <div>
                      <Label className="text-xs text-muted-foreground">KM Terakhir Ganti Oli</Label>
                      <Input type="number" min="0" value={form.last_oil_odometer} onChange={set("last_oil_odometer")} className="mt-1" placeholder="mis. 120000" data-testid="vehicle-oil-odometer-input" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          <div>
            <Label>Catatan (opsional)</Label>
            <Input value={form.notes} onChange={set("notes")} className="mt-1.5" data-testid="vehicle-notes-input" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={busy} className="bg-brand hover:bg-emerald-800" data-testid="submit-vehicle-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? "Simpan Perubahan" : "Tambah Kendaraan")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
